import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomTabNavigation from '../../../components/openplay/BottomTabNavigation';
import MapFilterToolbar from '../../../components/openplay/MapFilterToolbar';
import ContextualActionSheet from '../../../components/openplay/ContextualActionSheet';
import Icon from '../../../components/openplay/OpenPlayIcon';
import LocationSearchBar from '../../interactive-event-map/components/LocationSearchBar';
import ApplicationModal from '../../interactive-event-map/components/ApplicationModal';
import { supabase } from '../../../lib/supabaseClient';
import useNaverDiscoverData from '../../../hooks/useNaverDiscoverData';
import useNaverDiscoverLocation from '../../../hooks/useNaverDiscoverLocation';
import useEventTypeSubmission from '../../../hooks/useEventTypeSubmission';
import useEventTypeImages from '../../../hooks/useEventTypeImages';
import useNaverDiscoverMapMarkers from '../../../hooks/useNaverDiscoverMapMarkers';
import MapSidePanel from '../../../components/naver-discover/MapSidePanel';
import MobileMapListPanel from '../../../components/naver-discover/MobileMapListPanel';
import MapStatusOverlay from '../../../components/naver-discover/MapStatusOverlay';
import FullScreenLoadingState from '../../../components/naver-discover/FullScreenLoadingState';
import CreateEventMenu from '../../../components/naver-discover/CreateEventMenu';
import VenueSearchModal from '../../../components/naver-discover/VenueSearchModal';
import EventTypeModal from '../../../components/naver-discover/EventTypeModal';
import EventInfoModal from '../../../components/naver-discover/EventInfoModal';
import EventImageModal from '../../../components/naver-discover/EventImageModal';
import PlaceImageModal from '../../../components/naver-discover/PlaceImageModal';
import PlaceDetailModal from '../../../components/naver-discover/PlaceDetailModal';
import CreateEventIframeModal from '../../../components/naver-discover/CreateEventIframeModal';
import EventDetailsIframeModal from '../../../components/naver-discover/EventDetailsIframeModal';
import PicklingLogoMark from '../../../components/PicklingLogoMark';

const resolveNaverMapsCredential = ({ clientId, ncpKeyId, clientParam }) => {
  if (ncpKeyId) return { param: 'ncpKeyId', value: ncpKeyId };
  if (!clientId) return null;
  if (clientParam === 'ncpKeyId') return { param: 'ncpKeyId', value: clientId };
  if (clientParam === 'clientId') return { param: 'clientId', value: clientId };
  return { param: 'ncpClientId', value: clientId };
};

const loadNaverMapsScript = ({ param, value }) =>
  new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('No window'));
      return;
    }

    if (!param || !value) {
      reject(new Error('Missing Naver Maps credential'));
      return;
    }

    if (window.naver?.maps) {
      resolve();
      return;
    }

    const desiredSrc = `https://oapi.map.naver.com/openapi/v3/maps.js?${param}=${encodeURIComponent(value)}`;

    const existing = document.querySelector('script[data-naver-maps-sdk="true"]');
    if (existing) {
      const existingSrc = existing.getAttribute('src') || '';
      if (existingSrc && existingSrc !== desiredSrc) {
        try {
          existing.parentNode?.removeChild(existing);
        } catch {
          // ignore
        }
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error('Failed to load Naver Maps SDK')));
        return;
      }
    }

    const script = document.createElement('script');
    script.dataset.naverMapsSdk = 'true';
    script.async = true;
    script.defer = true;
    script.src = desiredSrc;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load Naver Maps SDK script: ${script.src}`));
    document.head.appendChild(script);
  });

  const waitForElementRef = (ref, { timeoutMs = 1500, intervalMs = 16 } = {}) =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (ref.current) {
        resolve(ref.current);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        reject(new Error('Map container not found'));
        return;
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });

const NaverDiscoverClient = ({ initialCreateType = null }) => {
  const fallbackEventImage = '/assets/images/no_image.png';
  const navigate = useNavigate();

  const naverMapsClientId = import.meta.env.VITE_NAVER_MAPS_CLIENT_ID;
  const naverMapsNcpKeyId = import.meta.env.VITE_NAVER_MAPS_NCP_KEY_ID;
  const naverMapsClientParam = import.meta.env.VITE_NAVER_MAPS_CLIENT_PARAM;
  const credential = resolveNaverMapsCredential({
    clientId: naverMapsClientId,
    ncpKeyId: naverMapsNcpKeyId,
    clientParam: naverMapsClientParam,
  });

  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedGroupEvents, setSelectedGroupEvents] = useState([]);
  const [selectedPlaceInfo, setSelectedPlaceInfo] = useState(null);
  const [showMobileEventsPanel, setShowMobileEventsPanel] = useState(false);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [eventDetailsUrl, setEventDetailsUrl] = useState('');
  const [pendingPanEvent, setPendingPanEvent] = useState(null);
  const [mapSideTab, setMapSideTab] = useState('events');
  const [showPlaceImageModal, setShowPlaceImageModal] = useState(false);
  const [placeImageIndex, setPlaceImageIndex] = useState(0);
  const [showEventImageModal, setShowEventImageModal] = useState(false);
  const [eventImageIndex, setEventImageIndex] = useState(0);
  const [popup, setPopup] = useState({ open: false, title: '', message: '' });
  const initialCreateHandledRef = useRef(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [createEventUrl, setCreateEventUrl] = useState(null);
  const [showCreateEventMenu, setShowCreateEventMenu] = useState(false);
  const [eventTypeModal, setEventTypeModal] = useState(null);
  const [eventTypeForm, setEventTypeForm] = useState({
    title: '',
    date: '',
    endDate: '',
    startTime: '',
    endTime: '',
    locationName: '',
    address: '',
    venueLat: '',
    venueLng: '',
    price: '',
    description: '',
    contact: '',
    url: '',
    imageDrafts: [],
    coverDraftId: '',
    imageUrls: [],
    coverImageUrl: '',
  });
  const [venueSearchOpen, setVenueSearchOpen] = useState(false);
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [venueSearchResults, setVenueSearchResults] = useState([]);
  const [venueSearchLoading, setVenueSearchLoading] = useState(false);
  const [venueSearchError, setVenueSearchError] = useState('');
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [filterState, setFilterState] = useState({
    dateRange: { start: null, end: null },
    availability: 'all',
  });
  const [eventTypeFilter, setEventTypeFilter] = useState([
    'open_game',
    'lesson',
  ]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem('openplay_sidebar_collapsed');
      setSidebarCollapsed(saved === '1');
    } catch {
      setSidebarCollapsed(false);
    }

    const handler = (e) => {
      setSidebarCollapsed(!!e?.detail?.collapsed);
    };

    try {
      window.addEventListener('openplay_sidebar_collapsed_change', handler);
    } catch {
      // ignore
    }

    return () => {
      try {
        window.removeEventListener('openplay_sidebar_collapsed_change', handler);
      } catch {
        // ignore
      }
    };
  }, []);

  const mapContainerRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRefs = useRef([]);
  const userLocationMarkerRef = useRef(null);
  const highlightCircleRef = useRef(null);
  const lastMapViewRef = useRef(null);
  const lastEventTypeModalRef = useRef(null);
  const suppressFitBoundsUntilRef = useRef(0);
  const suppressAutoRecenterUntilRef = useRef(0);

  const [naverMapsStatus, setNaverMapsStatus] = useState('idle');
  const [naverMapsError, setNaverMapsError] = useState(null);
  const [mapContainerSize, setMapContainerSize] = useState({ width: 0, height: 0 });
  const [mapWrapperSize, setMapWrapperSize] = useState({ width: 0, height: 0 });

  const { userLocation, isLocating, setUserLocation, handleUseCurrentLocation } = useNaverDiscoverLocation({
    mapInstanceRef,
    suppressAutoRecenterUntilRef,
    suppressFitBoundsUntilRef,
    onNotify: ({ title, message }) => setPopup({ open: true, title, message }),
  });

  const distanceKm = (a, b) => {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  };

  const {
    places,
    placesLoading,
    placesError,
    eventsLoading,
    eventsError,
    fetchEvents,
    fetchPlaces,
    filteredEvents,
    nearbyEvents,
    placeEventMap,
    placeList,
    listEvents,
    mapCenter,
  } = useNaverDiscoverData({
    eventTypeFilter,
    filterState,
    userLocation,
    distanceKm,
  });

  const normalizedMapSearchQuery = useMemo(() => String(mapSearchQuery || '').trim().toLowerCase(), [mapSearchQuery]);

  const matchesSearch = useCallback(
    (text) => {
      if (!normalizedMapSearchQuery) return true;
      return String(text || '').toLowerCase().includes(normalizedMapSearchQuery);
    },
    [normalizedMapSearchQuery]
  );

  const searchedEvents = useMemo(() => {
    const list = Array.isArray(listEvents) ? listEvents : [];
    if (!normalizedMapSearchQuery) return list;
    return list.filter((event) => {
      return (
        matchesSearch(event?.title) ||
        matchesSearch(event?.location) ||
        matchesSearch(event?.venueAddress)
      );
    });
  }, [listEvents, matchesSearch, normalizedMapSearchQuery]);

  const searchedPlaces = useMemo(() => {
    const list = Array.isArray(placeList) ? placeList : [];
    if (!normalizedMapSearchQuery) return list;
    return list.filter((place) => matchesSearch(place?.name) || matchesSearch(place?.address));
  }, [matchesSearch, normalizedMapSearchQuery, placeList]);

  const searchedPlacesById = useMemo(() => new Set((searchedPlaces || []).map((p) => String(p?.id))), [searchedPlaces]);

  const searchedPlacesRaw = useMemo(() => {
    if (!normalizedMapSearchQuery) return places;
    return (places || []).filter((p) => searchedPlacesById.has(String(p?.id)));
  }, [normalizedMapSearchQuery, places, searchedPlacesById]);

  const searchedEventsRaw = useMemo(() => {
    if (!normalizedMapSearchQuery) return nearbyEvents;
    const ids = new Set((searchedEvents || []).map((e) => String(e?.id)));
    return (nearbyEvents || []).filter((e) => ids.has(String(e?.id)));
  }, [nearbyEvents, normalizedMapSearchQuery, searchedEvents]);

  const searchedPlaceEventMap = useMemo(() => {
    if (!normalizedMapSearchQuery) return placeEventMap;
    const map = new Map();
    const sameSpot = (a, b) => {
      if (!a || !b) return false;
      const latDiff = Math.abs(a.latitude - b.latitude);
      const lngDiff = Math.abs(a.longitude - b.longitude);
      return latDiff < 0.00015 && lngDiff < 0.00015;
    };
    (searchedPlacesRaw || []).forEach((place) => {
      const eventsAtPlace = (searchedEventsRaw || []).filter((event) => sameSpot(place, event));
      map.set(place.id, eventsAtPlace);
    });
    return map;
  }, [normalizedMapSearchQuery, placeEventMap, searchedEventsRaw, searchedPlacesRaw]);

  const { submitting: eventTypeSubmitting, uploadStatus: eventTypeUploadStatus, submitEventType } = useEventTypeSubmission({
    type: eventTypeModal,
    fetchEvents,
    onNotify: ({ title, message }) => setPopup({ open: true, title, message }),
  });

  const {
    handleEventTypeImageSelect,
    handleRemoveEventTypeImage,
    handleSelectEventTypeCover,
  } = useEventTypeImages({
    setEventTypeForm,
    imageDrafts: eventTypeForm?.imageDrafts,
  });

  useEffect(() => {
    if (!initialCreateType || initialCreateHandledRef.current) return;
    initialCreateHandledRef.current = true;
    if (initialCreateType === 'open_game') {
      setCreateEventUrl('/openplay/session-creation-wizard');
      setShowCreateEventModal(true);
      return;
    }
    if (['lesson'].includes(initialCreateType)) {
      setEventTypeModal(initialCreateType);
    }
  }, [initialCreateType]);

  useEffect(() => {
    if (!eventTypeModal) {
      lastEventTypeModalRef.current = null;
      return;
    }
    if (lastEventTypeModalRef.current === eventTypeModal) return;
    lastEventTypeModalRef.current = eventTypeModal;
    setEventTypeForm({
      title: '',
      date: '',
      endDate: '',
      startTime: '',
      endTime: '',
      locationName: '',
      address: '',
      venueLat: '',
      venueLng: '',
      price: '',
      description: '',
      contact: '',
      url: '',
      imageDrafts: [],
      coverDraftId: '',
      imageUrls: [],
      coverImageUrl: '',
    });
  }, [eventTypeModal]);

  const handleVenueSearch = async (query) => {
    const q = String(query || '').trim();
    if (!q) return [];

    const normalize = (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((r) => {
        const roadAddress = String(r?.roadAddress || '').trim();
        const jibunAddress = String(r?.jibunAddress || '').trim();
        const englishAddress = String(r?.englishAddress || '').trim();
        const x = r?.x != null ? Number(r.x) : null;
        const y = r?.y != null ? Number(r.y) : null;
        return {
          roadAddress: roadAddress || null,
          jibunAddress: jibunAddress || null,
          englishAddress: englishAddress || null,
          x: Number.isFinite(x) ? x : null,
          y: Number.isFinite(y) ? y : null,
          placeName: r?.placeName ? String(r.placeName) : null,
        };
      });
    };

    try {
      const { data: placeData } = await supabase.functions.invoke('naver-place-search', {
        body: { query: q, display: 10 },
      });

      if (placeData?.ok) {
        const normalized = normalize(placeData?.results);
        if (normalized.length > 0) return normalized;
      }
    } catch {
      // ignore and fallback to geocode
    }

    const { data } = await supabase.functions.invoke('naver-geocode', {
      body: { query: q, count: 10, language: 'kor' },
    });

    if (!data?.ok) {
      throw new Error(data?.error || '네이버 지도 검색에 실패했습니다.');
    }

    return normalize(data?.results);
  };

  const handleOpenVenueSearch = () => {
    setVenueSearchQuery(String(eventTypeForm?.locationName || eventTypeForm?.address || '').trim());
    setVenueSearchResults([]);
    setVenueSearchError('');
    setVenueSearchOpen(true);
  };

  const handleRunVenueSearch = async () => {
    const q = String(venueSearchQuery || '').trim();
    if (!q) {
      setVenueSearchError('검색어를 입력하세요');
      return;
    }

    setVenueSearchLoading(true);
    setVenueSearchError('');
    try {
      const results = await handleVenueSearch(q);
      setVenueSearchResults(Array.isArray(results) ? results : []);
      if (!Array.isArray(results) || results.length === 0) {
        setVenueSearchError('검색 결과가 없습니다');
      }
    } catch (e) {
      setVenueSearchResults([]);
      setVenueSearchError(e?.message || '검색에 실패했습니다');
    } finally {
      setVenueSearchLoading(false);
    }
  };

  const handleSelectVenueResult = (row) => {
    const placeName = String(row?.placeName || '').trim();
    const address = String(row?.roadAddress || row?.jibunAddress || '').trim();
    const lat = row?.y != null ? String(row.y) : '';
    const lng = row?.x != null ? String(row.x) : '';

    setEventTypeForm((p) => ({
      ...(p || {}),
      locationName: placeName || String(p?.locationName || '').trim() || address,
      address,
      venueLat: lat,
      venueLng: lng,
    }));

    setVenueSearchOpen(false);
    setVenueSearchResults([]);
    setVenueSearchError('');
  };

  const toDatetimeLocalValue = (date, time) => {
    const d = String(date || '').trim();
    const t = String(time || '').trim();
    if (!d || !t) return '';
    return `${d}T${t}`;
  };

  const splitDatetimeLocalValue = (value) => {
    const v = String(value || '').trim();
    if (!v || !v.includes('T')) return { date: '', time: '' };
    const [d, t] = v.split('T');
    return { date: String(d || '').trim(), time: String(t || '').trim() };
  };

  const pad2 = (n) => String(n).padStart(2, '0');

  const addHoursToLocalDatetime = (date, time, hoursToAdd) => {
    const d = String(date || '').trim();
    const t = String(time || '').trim();
    const h = Number(hoursToAdd);
    if (!d || !t || !Number.isFinite(h)) return { date: '', time: '' };
    const base = new Date(`${d}T${t}:00`);
    if (Number.isNaN(base.getTime())) return { date: '', time: '' };
    const next = new Date(base.getTime() + h * 60 * 60 * 1000);
    const yyyy = next.getFullYear();
    const mm = pad2(next.getMonth() + 1);
    const dd = pad2(next.getDate());
    const hh = pad2(next.getHours());
    const mi = pad2(next.getMinutes());
    return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
  };

  useEffect(() => {
    if (!isHydrated) return;
    if (!credential) {
      setNaverMapsStatus('missing_key');
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        setNaverMapsError(null);
        setNaverMapsStatus('loading');

        await loadNaverMapsScript(credential);
        if (cancelled) return;

        if (!window.naver?.maps) {
          throw new Error('Naver Maps SDK not available after script load');
        }

        await waitForElementRef(mapContainerRef);
        if (cancelled) return;

        const naver = window.naver;

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new naver.maps.Map(mapContainerRef.current, {
            center: new naver.maps.LatLng(37.5665, 126.9780),
            zoom: 11,
            mapTypeControl: false,
            scaleControl: false,
            logoControl: true,
            zoomControl: false,
          });

          try {
            const wrapperEl = mapWrapperRef.current;
            const wrapperRect = wrapperEl?.getBoundingClientRect?.();
            if (wrapperRect) {
              setMapWrapperSize({ width: Math.round(wrapperRect.width), height: Math.round(wrapperRect.height) });
            }

            const rect = mapContainerRef.current.getBoundingClientRect();
            setMapContainerSize({ width: Math.round(rect.width), height: Math.round(rect.height) });

            const width = wrapperRect?.width ?? rect.width;
            const height = wrapperRect?.height ?? rect.height;
            if (width > 0 && height > 0) {
              mapInstanceRef.current.setSize(new naver.maps.Size(width, height));
            }
          } catch {
            // ignore
          }
        }

        setNaverMapsStatus('ready');
      } catch (e) {
        if (cancelled) return;
        console.error('[NaverDiscover] init failed:', e);
        setNaverMapsError(e);
        setNaverMapsStatus('error');
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [credential, isHydrated, loadNaverMapsScript, waitForElementRef]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const wrapperEl = mapWrapperRef.current;
    const containerEl = mapContainerRef.current;
    if (!wrapperEl || !containerEl) return;

    const updateSize = () => {
      try {
        const wrapperRect = wrapperEl.getBoundingClientRect();
        const containerRect = containerEl.getBoundingClientRect();

        setMapWrapperSize({ width: Math.round(wrapperRect.width), height: Math.round(wrapperRect.height) });
        setMapContainerSize({ width: Math.round(containerRect.width), height: Math.round(containerRect.height) });

        const width = wrapperRect.width || containerRect.width;
        const height = wrapperRect.height || containerRect.height;
        if (window.naver?.maps && mapInstanceRef.current && width > 0 && height > 0) {
          mapInstanceRef.current.setSize(new window.naver.maps.Size(width, height));
        }
      } catch {
        // ignore
      }
    };

    updateSize();

    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => updateSize());
      ro.observe(wrapperEl);
      return () => ro.disconnect();
    }

    const id = window.setInterval(updateSize, 500);
    return () => window.clearInterval(id);
  }, [isHydrated]);

  

  const naverMapsSdkSrc = useMemo(() => {
    if (!credential?.param || !credential?.value) return null;
    return `https://oapi.map.naver.com/openapi/v3/maps.js?${credential.param}=${encodeURIComponent(
      credential.value
    )}`;
  }, [credential?.param, credential?.value]);

  const currentOrigin = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return window.location.origin;
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  const handleSubmitEventType = async ({ payload, generateToken, images }) => {
    const res = await submitEventType({ payload, generateToken, images });
    if (res?.ok) {
      setEventTypeModal(null);
    }
  };

  const EVENT_TYPE_STYLES = {
    open_game: { color: 'var(--color-primary)' },
    lesson: { color: '#2563eb' },
  };

  const EVENT_TYPE_LABELS = {
    open_game: '오픈게임',
    lesson: '레슨',
  };

  const getEventTypeIconLabel = (eventType) => {
    switch (eventType) {
      case 'lesson':
        return 'L';
      case 'open_game':
      default:
        return 'OP';
    }
  };

  useEffect(() => {
    if (!pendingPanEvent) return;
    if (!naverMapsStatus || naverMapsStatus !== 'ready') return;
    if (showMobileEventsPanel) return;

    const id = window.setTimeout(() => {
      handlePanToEvent(pendingPanEvent, { adjustZoom: true, minZoom: 16, animate: true });
      setPendingPanEvent(null);
    }, 200);

    return () => window.clearTimeout(id);
  }, [naverMapsStatus, pendingPanEvent, showMobileEventsPanel]);

  const selectedEvent = selectedEventId ? listEvents.find((e) => e.id === selectedEventId) : null;

  const captureMapView = () => {
    if (!window.naver?.maps) return;
    if (!mapInstanceRef.current) return;
    try {
      const center = typeof mapInstanceRef.current.getCenter === 'function' ? mapInstanceRef.current.getCenter() : null;
      const zoom = typeof mapInstanceRef.current.getZoom === 'function' ? mapInstanceRef.current.getZoom() : null;

      const lat = typeof center?.lat === 'function' ? center.lat() : center?.lat;
      const lng = typeof center?.lng === 'function' ? center.lng() : center?.lng;

      if (typeof lat === 'number' && typeof lng === 'number') {
        lastMapViewRef.current = {
          lat,
          lng,
          zoom: Number.isFinite(zoom) ? zoom : null,
        };
      }
    } catch {
      // ignore
    }
  };

  const restoreMapView = () => {
    if (!window.naver?.maps) return;
    if (!mapInstanceRef.current) return;
    const snap = lastMapViewRef.current;
    if (!snap) return;
    try {
      mapInstanceRef.current.setCenter(new window.naver.maps.LatLng(snap.lat, snap.lng));
      if (Number.isFinite(snap.zoom)) {
        mapInstanceRef.current.setZoom(snap.zoom);
      }
    } catch {
      // ignore
    }
  };

  const handlePanToEvent = (event, { adjustZoom = false, minZoom = 16, animate = true } = {}) => {
    const lat = Number(event?.latitude);
    const lng = Number(event?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (!window.naver?.maps) return;
    if (!mapInstanceRef.current) return;

    try {
      suppressFitBoundsUntilRef.current = Date.now() + 15000;
      suppressAutoRecenterUntilRef.current = Date.now() + 15000;
      const nextCenter = new window.naver.maps.LatLng(lat, lng);
      const currentZoom = typeof mapInstanceRef.current.getZoom === 'function' ? mapInstanceRef.current.getZoom() : null;
      const nextZoom = adjustZoom
        ? (Number.isFinite(currentZoom) ? Math.max(minZoom, currentZoom) : minZoom)
        : currentZoom;

      if (animate && typeof mapInstanceRef.current.morph === 'function' && Number.isFinite(nextZoom)) {
        mapInstanceRef.current.morph(nextCenter, nextZoom);
      } else {
        mapInstanceRef.current.setCenter(nextCenter);
        if (adjustZoom && Number.isFinite(nextZoom)) {
          mapInstanceRef.current.setZoom(nextZoom);
        }
      }
    } catch {
      // ignore
    }
  };

  const handlePanToEventWithPopup = (event, { closeMobilePanel = false } = {}) => {
    handlePanToEvent(event, { adjustZoom: true, minZoom: 16, animate: true });
    if (window.naver?.maps && mapInstanceRef.current) {
      try {
        if (highlightCircleRef.current) {
          highlightCircleRef.current.setMap(null);
        }
        const lat = Number(event?.latitude);
        const lng = Number(event?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          highlightCircleRef.current = new window.naver.maps.Circle({
            map: mapInstanceRef.current,
            center: new window.naver.maps.LatLng(lat, lng),
            radius: 80,
            strokeColor: '#22c55e',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#22c55e',
            fillOpacity: 0.18,
          });
          window.setTimeout(() => {
            try {
              highlightCircleRef.current?.setMap(null);
            } catch {
              // ignore
            }
          }, 1200);
        }
      } catch {
        // ignore
      }
    }
    const matchedPlace = searchedPlacesRaw.find((place) => {
      const latDiff = Math.abs(place.latitude - Number(event?.latitude));
      const lngDiff = Math.abs(place.longitude - Number(event?.longitude));
      return latDiff < 0.00015 && lngDiff < 0.00015;
    });

    if (matchedPlace) {
      const eventsAtPlace = searchedPlaceEventMap.get(matchedPlace.id) || [];
      setSelectedPlaceInfo(matchedPlace);
      setSelectedGroupEvents(eventsAtPlace);
      setSelectedEventId(null);
      setShowBottomSheet(false);
    } else {
      setSelectedPlaceInfo(null);
      setSelectedGroupEvents([]);
      setSelectedEventId(event?.id || null);
      setShowBottomSheet(true);
    }
    if (closeMobilePanel) {
      setShowMobileEventsPanel(false);
    }
  };

  const handleMarkerClick = (eventId) => {
    const picked = searchedEvents.find((e) => String(e?.id) === String(eventId)) || null;
    if (picked) {
      const group = searchedEvents.filter((ev) => {
        const latDiff = Math.abs(Number(ev?.latitude) - Number(picked?.latitude));
        const lngDiff = Math.abs(Number(ev?.longitude) - Number(picked?.longitude));
        return latDiff < 0.00015 && lngDiff < 0.00015;
      });

      if (group.length > 1) {
        const matchedPlace = searchedPlacesRaw.find((place) => {
          const latDiff = Math.abs(place.latitude - Number(picked?.latitude));
          const lngDiff = Math.abs(place.longitude - Number(picked?.longitude));
          return latDiff < 0.00015 && lngDiff < 0.00015;
        });

        setSelectedPlaceInfo(matchedPlace || null);
        setSelectedGroupEvents(group);
        setSelectedEventId(null);
        setShowBottomSheet(false);
        return;
      }
    }

    setSelectedPlaceInfo(null);
    setSelectedEventId(eventId);
    setSelectedGroupEvents([]);
    setShowBottomSheet(true);
  };

  useNaverDiscoverMapMarkers({
    naverMapsStatus,
    mapInstanceRef,
    markerRefs,
    userLocationMarkerRef,
    suppressFitBoundsUntilRef,
    suppressAutoRecenterUntilRef,
    nearbyEvents: searchedEventsRaw,
    places: searchedPlacesRaw,
    placeEventMap: searchedPlaceEventMap,
    userLocation,
    getEventTypeIconLabel,
    eventTypeStyles: EVENT_TYPE_STYLES,
    onUserLocationClick: () => {
      if (!userLocation) return;
      handlePanToEvent({ latitude: userLocation.lat, longitude: userLocation.lng }, { adjustZoom: true, minZoom: 16, animate: true });
    },
    onSelectPlaceGroup: (place, eventsAtPlace) => {
      setSelectedPlaceInfo(place);
      setSelectedGroupEvents(Array.isArray(eventsAtPlace) ? eventsAtPlace : []);
      setSelectedEventId(null);
      setShowBottomSheet(false);
    },
    onSelectEvent: handleMarkerClick,
  });

  const handleApply = async (eventId) => {
    const known = listEvents.find((e) => String(e.id) === String(eventId));
    const token = String(known?.token || '').trim();
    if (token) {
      setEventDetailsUrl(`/public-session-page?token=${encodeURIComponent(token)}&open=apply&embed=1`);
      setShowEventDetailsModal(true);
      setSelectedEventId(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('session_invites')
        .select('token')
        .eq('id', eventId)
        .maybeSingle();
      if (error) {
        setPopup({ open: true, title: '오류', message: error?.message || '이벤트 정보를 불러오지 못했습니다.' });
        return;
      }
      const fetchedToken = String(data?.token || '').trim();
      if (!fetchedToken) {
        setPopup({ open: true, title: '오류', message: '공유 링크 토큰이 없습니다.' });
        return;
      }
      setEventDetailsUrl(`/public-session-page?token=${encodeURIComponent(fetchedToken)}&open=apply&embed=1`);
      setShowEventDetailsModal(true);
      setSelectedEventId(null);
    } catch {
      setPopup({ open: true, title: '오류', message: '이벤트 정보를 불러오는 중 오류가 발생했습니다.' });
    }
  };

  const handleShare = async (eventId) => {
    const known = listEvents.find((e) => String(e.id) === String(eventId));
    const token = String(known?.token || '').trim();
    const makeUrl = (tok) => `${window.location.origin}/public-session-page?token=${encodeURIComponent(tok)}`;
    if (token) {
      navigator.clipboard?.writeText(makeUrl(token));
      setPopup({ open: true, title: '공유 링크', message: '공유 링크가 복사되었습니다.' });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('session_invites')
        .select('token')
        .eq('id', eventId)
        .maybeSingle();
      if (error) {
        setPopup({ open: true, title: '오류', message: error?.message || '이벤트 정보를 불러오지 못했습니다.' });
        return;
      }
      const fetchedToken = String(data?.token || '').trim();
      if (!fetchedToken) {
        setPopup({ open: true, title: '오류', message: '공유 링크 토큰이 없습니다.' });
        return;
      }
      navigator.clipboard?.writeText(makeUrl(fetchedToken));
      setPopup({ open: true, title: '공유 링크', message: '공유 링크가 복사되었습니다.' });
    } catch {
      setPopup({ open: true, title: '오류', message: '이벤트 정보를 불러오는 중 오류가 발생했습니다.' });
    }
  };

  const handleViewDetails = async (eventId) => {
    const known = listEvents.find((e) => String(e.id) === String(eventId));
    const token = String(known?.token || '').trim();
    if (token) {
      setEventDetailsUrl(`/public-session-page?token=${encodeURIComponent(token)}&embed=1`);
      setShowEventDetailsModal(true);
      setSelectedEventId(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('session_invites')
        .select('token')
        .eq('id', eventId)
        .maybeSingle();
      if (error) {
        setPopup({ open: true, title: '오류', message: error?.message || '이벤트 정보를 불러오지 못했습니다.' });
        return;
      }
      const fetchedToken = String(data?.token || '').trim();
      if (!fetchedToken) {
        setPopup({ open: true, title: '오류', message: '공유 링크 토큰이 없습니다.' });
        return;
      }
      setEventDetailsUrl(`/public-session-page?token=${encodeURIComponent(fetchedToken)}&embed=1`);
      setShowEventDetailsModal(true);
      setSelectedEventId(null);
    } catch {
      setPopup({ open: true, title: '오류', message: '이벤트 정보를 불러오는 중 오류가 발생했습니다.' });
    }
  };

  const handleSearch = (query) => {
    setMapSearchQuery(String(query || ''));
  };

  const handleLocationQueryChange = (query) => {
    const q = String(query || '').trim();
    setMapSearchQuery(q);

    const normalized = q.toLowerCase();
    if (!normalized) {
      setLocationSuggestions([]);
      return;
    }

    const next = [];

    (Array.isArray(placeList) ? placeList : []).forEach((place) => {
      const name = String(place?.name || '').trim();
      const address = String(place?.address || '').trim();
      const hit =
        (name && name.toLowerCase().includes(normalized)) ||
        (address && address.toLowerCase().includes(normalized));
      if (!hit) return;

      const lat = Number(place?.latitude);
      const lng = Number(place?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      next.push({
        id: `place-${place?.id}`,
        name: name || address,
        address,
        x: lng,
        y: lat,
      });
    });

    (Array.isArray(listEvents) ? listEvents : []).forEach((event) => {
      const name = String(event?.title || '').trim();
      const address = String(event?.location || event?.venueAddress || '').trim();
      const hit =
        (name && name.toLowerCase().includes(normalized)) ||
        (address && address.toLowerCase().includes(normalized));
      if (!hit) return;

      const lat = Number(event?.latitude);
      const lng = Number(event?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      next.push({
        id: `event-${event?.id}`,
        name: name || address,
        address,
        x: lng,
        y: lat,
      });
    });

    setLocationSuggestions(next.slice(0, 10));
  };

  const handleSelectLocationSuggestion = (suggestion) => {
    const lat = Number(suggestion?.y);
    const lng = Number(suggestion?.x);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    try {
      if (suppressFitBoundsUntilRef) {
        suppressFitBoundsUntilRef.current = Date.now() + 15000;
      }
      suppressAutoRecenterUntilRef.current = Date.now() + 15000;
    } catch {
      // ignore
    }

    handlePanToEvent({ latitude: lat, longitude: lng }, { adjustZoom: true, minZoom: 16, animate: true });
  };

  const handleOpenCreateEvent = async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const authed = Boolean(data?.session?.user);
      if (!authed) {
        navigate('/openplay/user-authentication', { state: { from: '/openplay/event' } });
        return;
      }

      setShowCreateEventMenu(true);
    } catch {
      navigate('/openplay/user-authentication', { state: { from: '/openplay/event' } });
    }
  };

  const handleSelectEventType = (type) => {
    setShowCreateEventMenu(false);
    if (type === 'open_game') {
      setCreateEventUrl('/openplay/session-creation-wizard');
      setShowCreateEventModal(true);
      return;
    }
    setEventTypeModal(type);
  };

  


  if (!isHydrated) {
    return <FullScreenLoadingState message="지도를 불러오는 중..." />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className={sidebarCollapsed ? 'md:pl-[72px]' : 'md:pl-[240px]'}>
        <div className="relative">
          <div
            className="relative bg-muted h-[100dvh] md:h-[100vh]"
            style={{ minHeight: 400 }}
            ref={mapWrapperRef}
          >
            <div ref={mapContainerRef} className="absolute inset-0" />

            <MapSidePanel
              mapSideTab={mapSideTab}
              onChangeTab={setMapSideTab}
              placeList={searchedPlaces}
              listEvents={searchedEvents}
              userLocation={userLocation}
              getEventTypeIconLabel={getEventTypeIconLabel}
              eventTypeStyles={EVENT_TYPE_STYLES}
              fallbackEventImage={fallbackEventImage}
              onLocatePlace={(place) => {
                setSelectedPlaceInfo(place);
                setSelectedGroupEvents(searchedPlaceEventMap.get(place.id) || []);
                setShowBottomSheet(false);
                handlePanToEvent({ latitude: place.latitude, longitude: place.longitude }, { adjustZoom: true, minZoom: 16, animate: true });
              }}
              onLocateEvent={(event) => handlePanToEventWithPopup(event)}
              onApplyEvent={(event) => handleApply(event?.id)}
              onShareEvent={(event) => handleShare(event?.id)}
            />

            <div className="lg:hidden absolute top-[92px] left-4 z-[65]">
              <button
                type="button"
                onClick={() => {
                  captureMapView();
                  setShowMobileEventsPanel(true);
                  setTimeout(() => restoreMapView(), 50);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-border shadow-elevation-2 text-sm font-semibold text-foreground"
              >
                <Icon name="ListBulletIcon" size={18} />
                진행중 이벤트 {searchedEvents.length}개
              </button>
            </div>

            <MobileMapListPanel
              isOpen={showMobileEventsPanel}
              mapSideTab={mapSideTab}
              onChangeTab={setMapSideTab}
              onClose={() => setShowMobileEventsPanel(false)}
              placeList={searchedPlaces}
              listEvents={searchedEvents}
              userLocation={userLocation}
              getEventTypeIconLabel={getEventTypeIconLabel}
              eventTypeStyles={EVENT_TYPE_STYLES}
              fallbackEventImage={fallbackEventImage}
              onLocatePlace={(place) => {
                setSelectedPlaceInfo(place);
                setSelectedGroupEvents(searchedPlaceEventMap.get(place.id) || []);
                setShowMobileEventsPanel(false);
                setPendingPanEvent({ latitude: place.latitude, longitude: place.longitude });
              }}
              onLocateEvent={(event) => handlePanToEventWithPopup(event, { closeMobilePanel: true })}
              onApplyEvent={(event) => handleApply(event?.id)}
              onShareEvent={(event) => handleShare(event?.id)}
            />

            <div className="absolute top-4 left-4 z-[60] space-y-2 md:left-6">
              <div className="flex items-center gap-2 w-full max-w-[calc(100vw-140px)] md:max-w-md">
                <LocationSearchBar
                  onSearch={handleSearch}
                  onUseCurrentLocation={handleUseCurrentLocation}
                  onQueryChange={handleLocationQueryChange}
                  suggestions={locationSuggestions}
                  onSelectSuggestion={handleSelectLocationSuggestion}
                />
              </div>
            </div>

              <div className="absolute top-4 right-4 z-[70] flex flex-col gap-2">
                <div className="rounded-2xl border border-border bg-white/95 shadow-elevation-2 px-3 py-2 backdrop-blur">
                  <p className="text-[11px] font-semibold text-muted-foreground mb-2">이벤트 필터</p>
                  <div className="flex flex-col gap-1">
                    {Object.entries(EVENT_TYPE_LABELS).map(([type, label]) => {
                      const active = eventTypeFilter.includes(type);
                      const color = EVENT_TYPE_STYLES[type]?.color || 'var(--color-primary)';
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => {
                            setEventTypeFilter((prev) => {
                              if (prev.includes(type)) {
                                const next = prev.filter((item) => item !== type);
                                return next.length > 0 ? next : prev;
                              }
                              return [...prev, type];
                            });
                          }}
                          className={`flex items-center gap-2 px-2 py-1 rounded-lg text-xs font-semibold transition-smooth border ${
                            active
                              ? 'border-transparent shadow-elevation-1'
                              : 'border-border text-muted-foreground hover:text-foreground'
                          }`}
                          style={
                            active
                              ? {
                                  background: color,
                                  color: '#FFFFFF',
                                }
                              : undefined
                          }
                        >
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full border"
                            style={{ borderColor: active ? 'rgba(255,255,255,0.6)' : color, color: active ? '#FFFFFF' : color }}
                          >
                            <PicklingLogoMark
                              size={20}
                              className={active ? 'text-white' : 'text-primary-foreground'}
                              style={!active ? { color } : undefined}
                            />
                          </span>
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <MapStatusOverlay status={naverMapsStatus} error={naverMapsError} />

              {(eventsError || placesError) && (
                <div className="absolute inset-0 z-[80] flex items-center justify-center p-6">
                  <div className="max-w-md w-full bg-card rounded-xl border border-border shadow-elevation-2 p-6 text-center space-y-3">
                    <p className="text-base font-semibold text-foreground">데이터를 불러오지 못했습니다</p>
                    <p className="text-sm text-muted-foreground caption whitespace-pre-line">
                      {[eventsError, placesError].filter(Boolean).join('\n')}
                    </p>
                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => fetchEvents()}
                        disabled={eventsLoading}
                        className="flex-1 h-10 rounded-lg border border-border bg-background hover:bg-muted transition-smooth text-sm font-semibold disabled:opacity-60"
                      >
                        이벤트 재시도
                      </button>
                      <button
                        type="button"
                        onClick={() => fetchPlaces()}
                        disabled={placesLoading}
                        className="flex-1 h-10 rounded-lg border border-border bg-background hover:bg-muted transition-smooth text-sm font-semibold disabled:opacity-60"
                      >
                        장소 재시도
                      </button>
                    </div>
                  </div>
                </div>
              )}


          </div>
        </div>
      </div>

      <EventInfoModal
        isOpen={showBottomSheet}
        event={selectedEvent}
        fallbackEventImage={fallbackEventImage}
        onClose={() => {
          setShowBottomSheet(false);
          setSelectedEventId(null);
        }}
        onApply={() => handleApply(selectedEvent.id)}
        onViewDetails={() => handleViewDetails(selectedEvent.id)}
        onShowImage={(index) => {
          setEventImageIndex(index);
          setShowEventImageModal(true);
        }}
      />

      <EventImageModal
        isOpen={showEventImageModal}
        event={selectedEvent}
        fallbackEventImage={fallbackEventImage}
        eventImageIndex={eventImageIndex}
        setEventImageIndex={setEventImageIndex}
        onClose={() => setShowEventImageModal(false)}
      />

      <PlaceImageModal
        isOpen={showPlaceImageModal}
        place={selectedPlaceInfo}
        fallbackEventImage={fallbackEventImage}
        placeImageIndex={placeImageIndex}
        setPlaceImageIndex={setPlaceImageIndex}
        onClose={() => setShowPlaceImageModal(false)}
      />

      <PlaceDetailModal
        isOpen={selectedGroupEvents.length > 0 || Boolean(selectedPlaceInfo)}
        place={selectedPlaceInfo}
        events={selectedGroupEvents}
        userLocation={userLocation}
        fallbackEventImage={fallbackEventImage}
        onClose={() => {
          setSelectedPlaceInfo(null);
          setSelectedGroupEvents([]);
        }}
        onShowPlaceImage={(index) => {
          setPlaceImageIndex(index);
          setShowPlaceImageModal(true);
        }}
        onApply={handleApply}
        onViewDetails={handleViewDetails}
      />

      {selectedEvent && (
        <ApplicationModal
          isOpen={showApplicationModal}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedEventId(null);
          }}
          eventTitle={selectedEvent.title}
          paymentInfo={{
            price: selectedEvent?.price,
            bankName: selectedEvent?.bankName,
            accountNumber: selectedEvent?.accountNumber,
            accountHolder: selectedEvent?.accountHolder,
            paymentDeadline: selectedEvent?.paymentDeadline,
          }}
          onSubmit={handleApplicationSubmit}
        />
      )}

      <EventDetailsIframeModal
        isOpen={showEventDetailsModal}
        src={eventDetailsUrl}
        onClose={() => {
          setShowEventDetailsModal(false);
          setEventDetailsUrl(null);
        }}
      />

      <CreateEventIframeModal
        isOpen={showCreateEventModal}
        src={createEventUrl}
        onClose={() => {
          setShowCreateEventModal(false);
          setCreateEventUrl(null);
        }}
      />

      <EventTypeModal
        isOpen={Boolean(eventTypeModal)}
        type={eventTypeModal}
        form={eventTypeForm}
        submitting={eventTypeSubmitting}
        uploadStatus={eventTypeUploadStatus}
        onClose={() => setEventTypeModal(null)}
        onSubmit={handleSubmitEventType}
      />

      <VenueSearchModal
        isOpen={venueSearchOpen}
        query={venueSearchQuery}
        onChangeQuery={setVenueSearchQuery}
        onSubmit={handleRunVenueSearch}
        isLoading={venueSearchLoading}
        error={venueSearchError}
        results={venueSearchResults}
        onSelectResult={handleSelectVenueResult}
        onClose={() => setVenueSearchOpen(false)}
      />

      <div className="fixed right-4 bottom-24 md:bottom-8 z-[9000]">
        <CreateEventMenu
          isOpen={showCreateEventMenu}
          onClose={() => setShowCreateEventMenu(false)}
          onSelect={handleSelectEventType}
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenCreateEvent();
          }}
          className="flex items-center gap-2 px-4 py-3 rounded-full bg-primary text-primary-foreground shadow-elevation-4 hover:shadow-elevation-5 transition-smooth touch-target"
        >
          <Icon name="PlusIcon" size={18} />
          <span className="text-sm font-semibold">이벤트 등록</span>
        </button>
      </div>

      <BottomTabNavigation userRole="player" />
    </div>
  );
};

export default NaverDiscoverClient;
